/**
 * The admin menu: a few groups, each a page with tabs. One list drives the sidebar, the routes
 * and the redirects from the old flat URLs (bookmarks, OAuth return links, chat-app messages).
 */
export interface AdminTab {
  /** Path segment under the group. */
  path: string;
  label: string;
}
export interface AdminGroup {
  /** Path segment under /admin. */
  base: string;
  label: string;
  /** A single page has no tab bar. */
  tabs: AdminTab[];
}

export const ADMIN_GROUPS: AdminGroup[] = [
  {
    base: "people",
    label: "People & groups",
    tabs: [
      { path: "users", label: "Users" },
      { path: "groups", label: "Groups" },
      { path: "sso", label: "Sign-in" },
      { path: "audit", label: "Audit log" },
    ],
  },
  {
    base: "knowledge",
    label: "Knowledge",
    tabs: [
      { path: "shared", label: "Shared memory" },
      { path: "rules", label: "Sharing rules" },
      { path: "skills", label: "Skills" },
      { path: "sync", label: "Knowledge sync" },
    ],
  },
  {
    base: "assistants",
    label: "Assistants",
    tabs: [
      { path: "list", label: "Assistants" },
      { path: "models", label: "Models & providers" },
      { path: "briefings", label: "Briefings" },
      { path: "usage", label: "Usage" },
    ],
  },
  {
    base: "workspace",
    label: "Workspace",
    tabs: [
      { path: "hours-saved", label: "Hours saved" },
      { path: "messaging", label: "Messaging bots" },
      { path: "mcp", label: "MCP servers" },
      { path: "web-apps", label: "Web apps" },
      { path: "branding", label: "Branding" },
    ],
  },
];

/** A group's own page (People & groups → Groups → the group), at one of its tabs. */
export const groupPath = (id: string, tab: "overview" | "access" | "knowledge" | "budget" = "overview") => `/admin/people/groups/${id}/${tab}`;

/** Earlier URLs (the flat ones, then the first grouping) → where each page lives now. */
export const LEGACY_ADMIN_PATHS: Record<string, string> = {
  users: "people/users",
  groups: "people/groups",
  sso: "people/sso",
  audit: "people/audit",
  runtimes: "assistants/list",
  models: "assistants/models",
  usage: "assistants/usage",
  memory: "knowledge/shared",
  "knowledge/memory": "knowledge/shared",
  skills: "knowledge/skills",
  sync: "knowledge/sync",
  mcp: "workspace/mcp",
  messaging: "workspace/messaging",
  integrations: "workspace",
  "integrations/mcp": "workspace/mcp",
  "integrations/messaging": "workspace/messaging",
  branding: "workspace/branding",
};
